import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Link } from 'react-router-dom';
import { utils, write } from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';



const GuestList = () => {
    const [guests, setGuests] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalData, setModalData] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredData, setFilteredData] = useState([]);
    const handleErrors = useHandleErrors();
    const token = localStorage.getItem("token");
    const hasFetchedRef = useRef(false);


    const getGuestDetails = async () => {
        setLoading(true)
        try {
            const path = "/getGuestDetails";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.get(URL, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.status === 200) {
                return response.data;
            }
            else {
                handleErrors(response)
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 404) {
                    setData([]);
                } else {
                    handleErrors(error.response);
                }
            } else {
                toast.error("Something went wrong. Please try again later.", {
                    position: toast.POSITION.TOP_CENTER,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchGuests = async () => {
            try {
                const guestData = await getGuestDetails();
                setGuests(guestData);
                setData(guestData);
                setFilteredData(guestData)
            } catch (error) {
                if (error.response) {
                    handleErrors(error.response);
                } else {
                    toast.error("Something went wrong. Please try again later.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
                console.error('Error fetching guest data', error);
            } finally {
                setLoading(false);
            }
        };

        if (!hasFetchedRef.current) {
            fetchGuests();
            hasFetchedRef.current = true;
        }
    }, []);

    useEffect(() => {
        // Filter employees based on the search query
        setFilteredData(
            guests?.filter(guest =>
                guest.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                guest.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                guest.requestedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
                guest.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
                guest.guestEmail.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, guests]);

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

    const openModal = (item) => {
        setModalData(item);
    };
    const closeModal = (item) => {
        setModalData(null);
    };

    const sendEmail = (email) => {
        // Excel generation logic
        const ws = utils.json_to_sheet(filteredData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Guest');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        // Convert the Excel file to a Base64 string
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const base64data = reader.result.split(',')[1]; // Get the Base64 string without the prefix

            // Create a downloadable link for the attachment
            const link = document.createElement('a');
            link.href = reader.result;
            const today = new Date();
            const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
            const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
            const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
            const formattedDate = `${year}${month}${day}`;
            link.download = `WW360-Guest_Report_${formattedDate}.xlsx`;

            // Simulate a click to download the attachment
            link.click();

            // Prepare the mailto link (without attachment)
            const subject = "Assigned Asset List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);

        };
    };

    const shareData = {
        title: 'Guest List',
        text: 'Check out the guest list',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Guest");
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(blob, `WW360-Guest_Report_${formattedDate}.xlsx`);
    };

    const handleRecordsPerPageChange = (event) => {
        setRecordsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredData?.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredData?.length / recordsPerPage);


    const paginate = pageNumber => setCurrentPage(pageNumber);

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">View Guests</h3>
                        </div>
                        <div className="col-auto">
                            <Link to={'/GuestRegistartion'} className="btn btn-primary mt-lg-0 mt-4">
                                <i className="ri-body-scan-fill"></i> Register New
                            </Link>
                        </div>
                    </div>
                    {data?.length === 0 ? (
                        <div class="container">
                            <div class="row vh-50 justify-content-center align-items-center">
                                <div class="col-auto">
                                    <div class="no-data-wrapper">
                                        <div class="no-data-found">
                                            <i class="ri-file-damage-fill"></i>
                                            <div class="bounce"><i class="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-fill text-info"></i> Allows: First Name - Guest Email - Requested By
                                    </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Summary</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        type="button"
                                                        title="Email"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={() => sendEmail('')}
                                                    >
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Share"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={handleShare}
                                                    >
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={exportToExcel}>
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive asset-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>First Name</th>
                                                                    <th>Contact Number</th>
                                                                    <th>Guest Email</th>
                                                                    <th>Start Date</th>
                                                                    <th>End Date</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords?.map((guest, index) => (
                                                                    <tr key={guest.id}>
                                                                        <td>{index + 1}</td>
                                                                        <td>{guest.firstName}</td>
                                                                        <td>{guest.contactNumber}</td>
                                                                        <td>{guest.guestEmail}</td>
                                                                        <td>{moment(guest.startDate).format("DD-MM-YYYY")}</td>
                                                                        <td>{moment(guest.endDate).format("DD-MM-YYYY")}</td>
                                                                        <td className="text-center">
                                                                            <button className="btn btn-link text-info btn-action" type="button" title="List View" onClick={() => openModal(guest)}>
                                                                                <i className="ri-layout-horizontal-line"></i>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select className="form-control" value={recordsPerPage} onChange={handleRecordsPerPageChange}>
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {modalData && (
                        <div className="modal" tabIndex="-1" role="dialog" style={{ display: 'block', textAlign: 'center' }}>
                            <div className="modal-dialog" role="document">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Guest Details</h5>
                                        <button type="button" className="close" onClick={() => setModalData(null)}>
                                            <span>&times;</span>
                                        </button>
                                    </div>
                                    <div className="modal-body">
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">First Name:</span>
                                            <span className="modal-detail-value">{modalData.firstName}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Last Name:</span>
                                            <span className="modal-detail-value">{modalData.lastName}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Middle Name:</span>
                                            <span className="modal-detail-value">{modalData.middleName}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Contact Number:</span>
                                            <span className="modal-detail-value">{modalData.contactNumber}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Guest Email:</span>
                                            <span className="modal-detail-value">{modalData.guestEmail}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Start Date:</span>
                                            <span className="modal-detail-value">{modalData.startDate}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">End Date:</span>
                                            <span className="modal-detail-value">{modalData.endDate}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Number of Days:</span>
                                            <span className="modal-detail-value">
                                                {moment(modalData.endDate).diff(moment(modalData.startDate), 'days') === 0
                                                    ? 1
                                                    : moment(modalData.endDate).diff(moment(modalData.startDate), 'days')}
                                            </span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Reason for Visit:</span>
                                            <span className="modal-detail-value">{modalData.reasonForVisit}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Requested By:</span>
                                            <span className="modal-detail-value">{modalData.requestedBy}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Application Type:</span>
                                            <span className="modal-detail-value">{modalData.applicationType}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Department:</span>
                                            <span className="modal-detail-value">{modalData.department}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Project:</span>
                                            <span className="modal-detail-value">{modalData.project}</span>
                                        </div>
                                        <div className="modal-detail-row">
                                            <span className="modal-detail-key">Comments:</span>
                                            <span className="modal-detail-value">{modalData.comments}</span>
                                        </div>
                                    </div>
                                    <div className="modal-footer" style={{ display: 'block', textAlign: 'center' }}>
                                        <button type="button" className="btn btn-secondary" onClick={() => setModalData(null)}>Close</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GuestList;
